// Composants de formulaire PediatricsRegistrationStep
// components/form/FormComponents.tsx
import React from "react";

interface InputFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  required?: boolean;
  step?: string;
}

export const InputField: React.FC<InputFieldProps> = ({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  icon: Icon,
  required = false,
  step,
}) => {
  return (
    <div className="space-y-2">
      <label className="flex items-center text-sm font-semibold text-gray-700">
        {Icon && <Icon className="mr-2 text-blue-500" width={16} height={16} />}
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          step={step}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
        />
      </div>
    </div>
  );
};

interface TextAreaFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  rows?: number;
}

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  icon: Icon,
  rows = 3,
}) => {
  return (
    <div className="space-y-2">
      <label className="flex items-center text-sm font-semibold text-gray-700">
        {Icon && <Icon className="mr-2 text-blue-500" width={16} height={16} />}
        {label}
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none bg-white/80 backdrop-blur-sm"
      />
    </div>
  );
};

interface CheckboxFieldProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export const CheckboxField: React.FC<CheckboxFieldProps> = ({
  label,
  checked,
  onChange,
}) => {
  return (
    <label className="flex items-center space-x-3 p-4 rounded-xl border-2 bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-100 cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-[1.02]">
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
        />
        <div
          className={`w-5 h-5 rounded-lg border-2 transition-all duration-200 flex items-center justify-center ${
            checked
              ? "bg-gradient-to-br from-blue-500 to-blue-600 border-blue-600"
              : "border-gray-300 bg-white"
          }`}
        >
          {checked && (
            <svg
              className="w-3 h-3 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
      </div>
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </label>
  );
};

interface RadioFieldProps {
  label: string;
  options: { value: string; label: string }[];
  selectedValue: string;
  onChange: (value: string) => void;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

export const RadioField: React.FC<RadioFieldProps> = ({
  label,
  options,
  selectedValue,
  onChange,
  icon: Icon,
}) => {
  return (
    <div className="space-y-3">
      <label className="flex items-center text-sm font-semibold text-gray-700">
        {Icon && <Icon className="mr-2 text-blue-500" width={16} height={16} />}
        {label}
      </label>
      <div className="flex flex-wrap gap-3">
        {options.map((option) => (
          <label
            key={option.value}
            className="flex items-center space-x-2 cursor-pointer"
          >
            <input
              type="radio"
              value={option.value}
              checked={selectedValue === option.value}
              onChange={(e) => onChange(e.target.value)}
              className="sr-only"
            />
            <div
              className={`w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                selectedValue === option.value
                  ? "bg-blue-500 border-blue-500"
                  : "border-gray-300 bg-white"
              }`}
            >
              {selectedValue === option.value && (
                <div className="w-2 h-2 rounded-full bg-white"></div>
              )}
            </div>
            <span className="text-sm font-medium text-gray-700">
              {option.label}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};